import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { 
  BarChart3, Download, BadgeCheck, Star, Eye, RefreshCw, 
  TrendingUp, Database, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const FeedbackDataDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/material-feedback/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback stats:', error);
      toast.error('Failed to load feedback statistics');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/material-feedback/export?feedback_type=${type}`, {
        withCredentials: true
      });
      
      // Create download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback_${type}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} ${type} records`);
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
        <p className="text-muted-foreground">Failed to load feedback data</p>
        <Button onClick={fetchStats} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="heading-display text-2xl font-medium">Material Feedback Intelligence</h2>
          <p className="text-muted-foreground mt-1">
            Track user interactions for AI training and material intelligence
          </p>
        </div>
        <Button variant="outline" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Interactions */}
        <div className="bg-white border border-border p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[var(--gold)]/20 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-[var(--gold)]" />
            </div>
            <p className="text-sm text-muted-foreground">Total Interactions</p>
          </div>
          <p className="text-3xl font-bold">{stats.totals.total.toLocaleString()}</p>
        </div>

        {/* Confirmed Matches - HIGHEST VALUE */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800">Confirmed Matches</p>
              <p className="text-xs text-green-600">HIGHEST VALUE</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-green-700">{stats.totals.confirmed_matches}</p>
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-3 w-full border-green-300 text-green-700 hover:bg-green-100"
            onClick={() => exportData('confirmed_match')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" /> Export for Training
          </Button>
        </div>

        {/* Ratings - HIGH VALUE */}
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-sr-gold rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800">Star Ratings</p>
              <p className="text-xs text-amber-600">HIGH VALUE</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-amber-700">{stats.totals.ratings}</p>
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-3 w-full border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => exportData('rating')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {/* Views - LOW VALUE */}
        <div className="bg-stone-50 border border-stone-200 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-stone-400 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-stone-700">View Events</p>
              <p className="text-xs text-stone-500">LOW VALUE</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-stone-600">{stats.totals.views}</p>
        </div>
      </div>

      {/* Top Confirmed Materials */}
      {stats.top_confirmed_materials?.length > 0 && (
        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-green-500" />
            Top Confirmed Materials (Best Training Data)
          </h3>
          <div className="space-y-2">
            {stats.top_confirmed_materials.map((item, idx) => (
              <div key={item.material_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="font-medium">{item.material_id}</span>
                </div>
                <span className="text-green-600 font-medium">{item.confirmations} confirmations</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Rated Materials */}
      {stats.top_rated_materials?.length > 0 && (
        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-sr-gold" />
            Top Rated Materials
          </h3>
          <div className="space-y-2">
            {stats.top_rated_materials.map((item, idx) => (
              <div key={item.material_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="font-medium">{item.material_id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= item.avg_rating ? 'text-sr-gold fill-amber-500' : 'text-stone-300'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">({item.rating_count} ratings)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {stats.recent_feedback?.length > 0 && (
        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[var(--gold)]" />
            Recent Feedback Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Material ID</th>
                  <th className="text-left py-2 px-3">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_feedback.slice(0, 10).map((fb) => (
                  <tr key={fb.feedback_id} className="border-b border-border last:border-0">
                    <td className="py-2 px-3">
                      {fb.feedback_type === 'confirmed_match' && (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <BadgeCheck className="w-4 h-4" /> Confirmed
                        </span>
                      )}
                      {fb.feedback_type === 'rating' && (
                        <span className="inline-flex items-center gap-1 text-amber-600">
                          <Star className="w-4 h-4" /> Rating
                        </span>
                      )}
                      {fb.feedback_type === 'view' && (
                        <span className="inline-flex items-center gap-1 text-stone-500">
                          <Eye className="w-4 h-4" /> View
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{fb.material_id || '-'}</td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {new Date(fb.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Priority Info */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-6">
        <h3 className="font-medium mb-4">Data Priority for AI Training</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center shrink-0">
              <BadgeCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-medium text-green-700">HIGHEST VALUE</p>
              <p className="text-muted-foreground">Confirmed matches - User explicitly confirms this IS the material</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-sr-gold rounded flex items-center justify-center shrink-0">
              <Star className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-medium text-amber-700">HIGH VALUE</p>
              <p className="text-muted-foreground">Star ratings - User rates match quality 1-5</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-stone-400 rounded flex items-center justify-center shrink-0">
              <Eye className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-medium text-stone-600">LOW VALUE</p>
              <p className="text-muted-foreground">View events - User saw results (implicit signal)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDataDashboard;
